import type { ComponentChildren, JSX } from 'preact';
import { offProps } from './OffNote.tsx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  disabled?: boolean;
  onClick?: (e: MouseEvent) => void;
  children?: ComponentChildren;
  style?: JSX.CSSProperties;
  full?: boolean;
  /** The walkthrough is pointing at this button (D44): an accent ring, still tappable. */
  mark?: boolean;
  /**
   * D52 and D143: why this action cannot be taken. The button greys itself and the reason
   * is drawn beneath it, one way, in every card on the app. Before D143 each card wrote
   * its own line in its own place, so the same rule looked like five different rules.
   */
  reason?: string;
  /**
   * D235: the reason a disabled button gives when pressed, where the screen already draws
   * it somewhere else (a sheet's amber line, the panel's reason row) and a second copy
   * under the button would say it twice. `reason` is used when both are given.
   */
  explain?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

const VARIANTS: Record<ButtonVariant, JSX.CSSProperties> = {
  primary: { background: 'var(--accent)', color: 'var(--accent-ink)' },
  secondary: { background: 'var(--surface)', color: 'var(--ink)', borderColor: 'var(--line)' },
  ghost: { background: 'transparent', color: 'var(--accent)' },
  danger: { background: 'var(--red-bg)', color: 'var(--red)' },
};

/**
 * The one line a disabled action gives for itself (D143). Full-strength `--ink`, not the
 * secondary ink: D152 found the previous treatment unreadable and `--ink-2` on
 * `--surface-2` falls short of 4.5:1 too, so the reason takes the same ink as everything
 * else and the disabled state is carried by the fill instead.
 */
export const REASON_STYLE: JSX.CSSProperties = {
  font: '400 11px/1.3 var(--font-ui)',
  color: 'var(--ink)',
  minWidth: 0,
  overflowWrap: 'anywhere',
};

/**
 * D180. The frame around one choice on a sheet that offers several.
 *
 * D171 took the fill off every disabled control, which is right: a fill means chosen, or
 * pressable. On a Buy sheet where the world can afford neither the purchase nor the lease
 * both buttons go flat at once, and the sheet stops looking like a sheet — the screenshot
 * D180 was raised on is a page of writing with no buttons anywhere in it. The answer is
 * not to give a dead control its fill back. It is that the structure belongs to the
 * choice, not to the button: each choice is a bounded block carrying its price rows, its
 * reason and its action, so a sheet of two choices reads as two choices whether or not
 * either can be taken. The frame is a container, never a control, so it carries a hairline
 * and no ground, and says nothing about pressing.
 */
export const CHOICE_FRAME: JSX.CSSProperties = {
  display: 'grid',
  gap: 6,
  padding: 10,
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--line)',
};

/** Flat button. primary (accent fill), secondary (line), ghost (text). 44px tall in a card, 48px at full width (D458), 32 for small. */
export function Button({
  variant = 'secondary',
  size = 'md',
  disabled,
  onClick,
  children,
  style,
  full,
  mark,
  reason,
  explain,
  ...rest
}: ButtonProps) {
  // D458: 44 in a card, 48 only at full width.
  const h = size === 'sm' ? 32 : full ? 48 : 44;
  const off = disabled || !!reason;
  const base: JSX.CSSProperties = {
    height: h,
    padding: size === 'sm' ? '0 10px' : '0 16px',
    borderRadius: 'var(--radius-md)',
    font: `${size === 'sm' ? 500 : 600} ${size === 'sm' ? 12 : 13}px/1 var(--font-ui)`,
    cursor: off ? 'default' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: full ? '100%' : undefined,
    border: '1px solid transparent',
    transition: 'background var(--dur) var(--ease)',
    whiteSpace: 'nowrap',
  };
  const button = (
    <button
      type="button"
      // D235: never the native attribute, which swallows the press. Marked instead, it
      // takes the one disabled look from base.css (grey box, struck through), and a press
      // shows its reason beside it rather than running the action.
      {...offProps(off, reason ?? explain)}
      class={mark ? 'walk-mark' : undefined}
      // Nor does the action run if the note's listener is ever missing.
      onClick={off ? undefined : onClick}
      aria-label={rest['aria-label']}
      data-testid={rest['data-testid']}
      style={{
        ...base,
        ...VARIANTS[variant],
        ...(reason ? undefined : style),
      }}
      onMouseDown={(e) => {
        if (!off) e.currentTarget.style.filter = 'brightness(.92)';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.filter = '';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = '';
      }}
    >
      {children}
    </button>
  );
  if (!reason) return button;
  // D52: the reason sits under the control it belongs to, on every screen.
  return (
    <span
      style={{
        display: 'grid',
        gap: 4,
        justifyItems: full ? 'stretch' : 'start',
        minWidth: 0,
        ...style,
      }}
    >
      {button}
      <span style={REASON_STYLE}>{reason}</span>
    </span>
  );
}
